/**
 * Core-100 R4 wave 2 — CORE-O (B): a learning-history entry keeps OPENED, ENGAGED, PROGRESSED and COMPLETED apart and
 * states HOW completion was determined, including whether a human verified it. They are never flattened into
 * "completed training", and none of them is proficiency or certification (constitution CORE-O; CORE-L owns
 * credentials).
 *
 * Input: the durable session row (training_consumption_sessions). Pure — no I/O.
 */
export interface LearningSessionStageInput {
  opened_at: string | null;
  created_at?: string | null;
  engaged_at?: string | null;
  progress_percent?: number | null;
  completed_at?: string | null;
  confirmed_at?: string | null;
  completion_status?: string | null;
  completion_confidence?: string | null;
  manual_marked_complete_at?: string | null;
  manager_verified_at?: string | null;
  extension_evidence_summary?: unknown;
}

export type CompletionDetermination =
  | 'manager_verified'   // a manager attested the completion decision (human)
  | 'self_reported'      // the person marked it complete
  | 'observed'           // the Training Companion observed it
  | 'recorded'           // recorded complete by the system, with the stated evidence strength
  | 'not_completed';

export interface LearningHistoryStages {
  openedAt: string | null;
  engagedAt: string | null;
  progressPercent: number | null;
  completedAt: string | null;
  completed: boolean;
  determination: CompletionDetermination;
  /** Plain words for "how completion was determined"; null when not completed. */
  determinationLabel: string | null;
  /** Why an attempt ended without completion (replaced, abandoned, skipped), else null. */
  endedWithout: string | null;
}

const ENDED_WITHOUT: Record<string, string> = {
  superseded: 'Replaced by a later attempt',
  abandoned: 'Not finished',
  skipped: 'Skipped',
};

const CONFIDENCE_WORDS: Record<string, string> = {
  verified: 'strong evidence',
  likely: 'some evidence',
  unverified: 'no supporting evidence',
  suspicious: 'evidence that needs review',
};

export function learningHistoryStages(row: LearningSessionStageInput): LearningHistoryStages {
  const status = row.completion_status ?? null;
  const completed = status === 'completed';
  const completedAt = completed ? (row.completed_at ?? row.confirmed_at ?? row.manager_verified_at ?? row.manual_marked_complete_at ?? null) : null;
  let determination: CompletionDetermination = 'not_completed';
  let determinationLabel: string | null = null;
  if (completed) {
    if (row.manager_verified_at) {
      determination = 'manager_verified';
      determinationLabel = 'A manager verified the completion (a human check; not a certification)';
    } else if (row.manual_marked_complete_at) {
      determination = 'self_reported';
      determinationLabel = 'You marked it complete (self-reported)';
    } else if (row.extension_evidence_summary) {
      determination = 'observed';
      determinationLabel = 'Observed by the Training Companion';
    } else {
      determination = 'recorded';
      const words = CONFIDENCE_WORDS[row.completion_confidence ?? 'unverified'] ?? 'no supporting evidence';
      determinationLabel = `Recorded complete, with ${words}`;
    }
  }
  return {
    openedAt: row.opened_at ?? row.created_at ?? null,
    engagedAt: row.engaged_at ?? null,
    progressPercent: typeof row.progress_percent === 'number' ? row.progress_percent : null,
    completedAt,
    completed,
    determination,
    determinationLabel,
    endedWithout: status && ENDED_WITHOUT[status] ? ENDED_WITHOUT[status] : null,
  };
}
